import { useTranslation } from 'react-i18next';
import { useMemo, useEffect } from 'react';
import { translateBackendData, translateBackendDataBatch, clearTranslationCache } from '../utils/translate-middleware';

/**
 * Custom hook to automatically translate backend data
 * 
 * @example
 * const translatedProduct = useTranslatedData(product);
 * const translatedProducts = useTranslatedData(products);
 */
export function useTranslatedData<T>(data: T | null | undefined): T | null | undefined {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    // Clear cache when language changes
    useEffect(() => {
        clearTranslationCache();
    }, [currentLanguage]);

    const translatedData = useMemo(() => {
        if (!data) {
            return data;
        }

        // Handle arrays
        if (Array.isArray(data)) {
            return translateBackendDataBatch(data, t, currentLanguage) as T;
        }

        // Handle single objects
        return translateBackendData(data, t, currentLanguage);
    }, [data, t, currentLanguage]); // Keep all dependencies for correctness

    return translatedData;
}

/**
 * Hook specifically for translating product/category names
 * Returns both the translated name and a function to translate any field
 */
export function useProductTranslation(productName: string | undefined) {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    const translatedName = useMemo(() => {
        if (!productName) return productName;

        const camelName = productName
            .toLowerCase()
            .replace(/[^a-z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
            .replace(/^[A-Z]/, (chr) => chr.toLowerCase());

        const key = `dynamicContent.${camelName}.title`;
        const translated = t(key);

        return translated !== key ? translated : productName;
    }, [productName, t]); // currentLanguage changes trigger re-render automatically

    const translateField = useMemo(() => {
        return (fieldValue: string, fieldType: 'title' | 'description' = 'title') => {
            if (!fieldValue) return fieldValue;

            const camelName = fieldValue
                .toLowerCase()
                .replace(/[^a-z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
                .replace(/^[A-Z]/, (chr) => chr.toLowerCase());

            const key = `dynamicContent.${camelName}.${fieldType}`;
            const translated = t(key);

            return translated !== key ? translated : fieldValue;
        };
    }, [t]); // currentLanguage changes trigger re-render automatically

    return {
        translatedName,
        translateField,
        currentLanguage
    };
}
